import React, { useEffect, useState, useCallback } from 'react';
import { Truck, Package, CheckCircle, TrendingUp, TrendingDown, Minus, MapPin, Users, X } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import api from '../api';

const C = { blue:'#0284c7', green:'#16a34a', red:'#dc2626', amber:'#f59e0b', purple:'#7c3aed', slate:'#64748b' };
const money = n => 'AED ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const num = n => Number(n || 0).toLocaleString();
const mins = n => { const v = Math.round(Number(n || 0)); if (!v) return '0m'; const h = Math.floor(v/60), m = v%60; return h ? `${h}h ${m}m` : `${m}m`; };
const iso = d => d.toISOString().split('T')[0];
const shortDate = d => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric' });
const card = { background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' };
const th = { padding:'10px 14px', textAlign:'left', color:'#64748b', fontWeight:500, fontSize:12 };
const td = { padding:'11px 14px', color:'#475569', fontSize:13 };

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div style={{ ...card, padding:20, flex:1, minWidth:180 }}>
    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
      <div style={{ background: color + '20', borderRadius:10, padding:10 }}><Icon size={20} color={color} /></div>
      <div>
        <div style={{ fontSize:12, color:'#64748b' }}>{label}</div>
        <div style={{ fontSize:22, fontWeight:700, color:'#1e293b' }}>{value}</div>
        {sub && <div style={{ fontSize:11, color:'#94a3b8' }}>{sub}</div>}
      </div>
    </div>
  </div>
);

const CompareCard = ({ label, value, prev, color, format = num, invert = false, onClick }) => {
  const cur = Number(value || 0), old = Number(prev || 0);
  const diff = old > 0 ? Math.round(((cur - old) / old) * 100) : (cur > 0 ? 100 : 0);
  const good = invert ? diff < 0 : diff > 0;
  const Icon = diff === 0 ? Minus : diff > 0 ? TrendingUp : TrendingDown;
  const tint = diff === 0 ? C.slate : good ? C.green : C.red;
  return (
    <div onClick={onClick} style={{ ...card, padding:18, flex:1, minWidth:170, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:21, fontWeight:700, color: color || '#1e293b' }}>{format(cur)}</div>
      <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:6, fontSize:11, color:tint, fontWeight:600 }}>
        <Icon size={13} /> {diff === 0 ? 'No change' : `${Math.abs(diff)}% vs previous`}
      </div>
    </div>
  );
};

const Panel = ({ title, children, note }) => (
  <div style={{ ...card, overflow:'hidden', marginBottom:16 }}>
    <div style={{ padding:'14px 20px', borderBottom:'1px solid #f1f5f9' }}>
      <h2 style={{ fontSize:14, fontWeight:600, color:'#1e293b', margin:0 }}>{title}</h2>
      {note && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{note}</div>}
    </div>
    <div style={{ padding:16 }}>{children}</div>
  </div>
);

const Empty = ({ text }) => <div style={{ padding:28, textAlign:'center', color:'#94a3b8', fontSize:13 }}>{text}</div>;

const PRESETS = [
  { key:'today', label:'Today', days:0 },
  { key:'yesterday', label:'Yesterday', days:-1 },
  { key:'7', label:'Last 7 days', days:7 },
  { key:'30', label:'Last 30 days', days:30 },
  { key:'90', label:'Last 90 days', days:90 },
];

export default function Dashboard() {
  const [tab, setTab] = useState('today');
  const todayIso = iso(new Date());

  const [preset, setPreset] = useState('30');
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(todayIso);

  const [todayData, setTodayData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [perf, setPerf] = useState(null);
  const [problems, setProblems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('delivered');

  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    api.get('/analytics/dashboard?date=' + todayIso).then(r => setTodayData(r.data)).catch(() => {});
  }, [todayIso]);

  const applyPreset = (p) => {
    setPreset(p.key);
    const now = new Date();
    if (p.days === 0) { setFrom(todayIso); setTo(todayIso); }
    else if (p.days === -1) { const y = iso(new Date(now.getTime() - 86400000)); setFrom(y); setTo(y); }
    else { setFrom(iso(new Date(now.getTime() - (p.days - 1) * 86400000))); setTo(todayIso); }
    setTrends(null); setPerf(null); setProblems(null);
  };

  const setCustom = (which, val) => {
    setPreset('custom');
    if (which === 'from') setFrom(val); else setTo(val);
    setTrends(null); setPerf(null); setProblems(null);
  };

  const loadRange = useCallback(async () => {
    if (tab === 'today') return;
    setLoading(true);
    const q = `?from=${from}&to=${to}`;
    try {
      if (tab === 'trends' && !trends) { const { data } = await api.get('/analytics/trends' + q); setTrends(data); }
      if (tab === 'drivers' && !perf) { const { data } = await api.get('/analytics/driver-performance' + q); setPerf(data); }
      if (tab === 'problems' && !problems) { const { data } = await api.get('/analytics/problems' + q); setProblems(data); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tab, from, to, trends, perf, problems]);

  useEffect(() => { loadRange(); }, [loadRange]);

  const openDrill = async (opts) => {
    setDrill(opts); setDrillData(null); setDrillLoading(true);
    const p = new URLSearchParams();
    if (opts.date) p.set('date', opts.date); else { p.set('from', from); p.set('to', to); }
    if (opts.driver_id) p.set('driver_id', opts.driver_id);
    if (opts.status) p.set('status', opts.status);
    try {
      const { data } = await api.get('/analytics/invoices-detail?' + p.toString());
      setDrillData(data);
    } catch (e) { setDrillData({ count:0, total_value:0, invoices:[] }); }
    finally { setDrillLoading(false); }
  };

  const t = todayData?.totals || {};

  const series = (trends?.series || []).map(s => ({
    date: shortDate(s.date),
    rawDate: String(s.date).split('T')[0],
    Delivered: Number(s.delivered),
    Cancelled: Number(s.cancelled),
    Rescheduled: Number(s.rescheduled),
    Value: Number(s.value),
    Travel: Math.round(Number(s.avg_travel)),
    Shop: Math.round(Number(s.avg_service)),
  }));

  const drivers = [...(perf?.drivers || [])].sort((a, b) => Number(b[sortBy] || 0) - Number(a[sortBy] || 0));

  const barClick = (status) => (entry) => {
    if (!entry || !entry.payload) return;
    const p = entry.payload;
    if (p.rawDate) openDrill({ date:p.rawDate, status, title:`${status} — ${p.date}` });
    else if (p.driverId) openDrill({ driver_id:p.driverId, status, title:`${status} — ${p.name}` });
  };

  return (
    <div style={{ padding:24 }}>
      <div style={{ marginBottom:18 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:'#1e293b', margin:0 }}>Dashboard</h1>
        <p style={{ color:'#64748b', fontSize:13, margin:'4px 0 0' }}>
          {new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
        </p>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        {[['today','Today'],['trends','Trends'],['drivers','Drivers'],['problems','Problems']].map(([k,label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding:'8px 16px', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600,
            background: tab===k ? C.blue : '#fff', color: tab===k ? '#fff' : '#64748b',
            boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>{label}</button>
        ))}
      </div>

      {tab !== 'today' && (
        <div style={{ ...card, padding:'12px 16px', marginBottom:18, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => applyPreset(p)} style={{
              padding:'6px 12px', border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
              background: preset===p.key ? '#1e293b' : '#f1f5f9', color: preset===p.key ? '#fff' : '#64748b' }}>
              {p.label}</button>
          ))}
          <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center', fontSize:12, color:'#64748b' }}>
            <input type="date" value={from} max={to} onChange={e => setCustom('from', e.target.value)}
              style={{ padding:'6px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12 }} />
            <span>to</span>
            <input type="date" value={to} min={from} max={todayIso} onChange={e => setCustom('to', e.target.value)}
              style={{ padding:'6px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12 }} />
          </div>
        </div>
      )}

      {loading && <div style={{ color:'#94a3b8', fontSize:13, marginBottom:12 }}>Loading...</div>}

      {tab === 'today' && (
        <>
          <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap' }}>
            <StatCard icon={Truck} label="Active drivers" value={t.total_sessions || 0} color={C.blue} sub="Today" />
            <StatCard icon={Package} label="Total invoices" value={t.total_invoices || 0} color={C.purple} sub="Today" />
            <StatCard icon={CheckCircle} label="Delivered" value={t.delivered || 0} color={C.green} sub="Completed" />
            <StatCard icon={TrendingUp} label="Invoice value" value={money(t.total_value)} color="#ea580c" sub="Today total" />
          </div>
          <div style={{ ...card, overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9' }}>
              <h2 style={{ fontSize:15, fontWeight:600, color:'#1e293b', margin:0 }}>Driver status — today</h2>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>Click a row to see that driver's stops</div>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f8fafc' }}>
                {['Driver','Status','Stops','Completed','Invoice Value','Distance'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(todayData?.sessions || []).map(s => (
                  <tr key={s.id} style={{ borderTop:'1px solid #f1f5f9', cursor:'pointer' }}
                    onClick={() => openDrill({ date:todayIso, driver_id:s.driver_id, title:`${s.driver_name} — today` })}>
                    <td style={{ ...td, fontWeight:500, color:'#1e293b' }}>{s.driver_name}</td>
                    <td style={td}>
                      <span style={{ padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:500,
                        background: s.status==='completed' ? '#dcfce7' : s.status==='in_progress' ? '#dbeafe' : '#fef9c3',
                        color: s.status==='completed' ? C.green : s.status==='in_progress' ? '#1d4ed8' : '#854d0e' }}>
                        {s.status.replace('_',' ')}</span>
                    </td>
                    <td style={td}>{s.total_stops}</td>
                    <td style={{ ...td, color:C.green, fontWeight:500 }}>{s.completed_stops}</td>
                    <td style={{ ...td, color:'#1e293b' }}>{money(s.total_value)}</td>
                    <td style={td}>{s.total_distance || 0} km</td>
                  </tr>
                ))}
                {!todayData?.sessions?.length && <tr><td colSpan={6}><Empty text="No deliveries today" /></td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'trends' && trends && (
        <>
          <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
            <CompareCard label="Delivered" value={trends.current.delivered} prev={trends.previous.delivered} color={C.green}
              onClick={() => openDrill({ status:'delivered', title:'Delivered' })} />
            <CompareCard label="Success rate" value={trends.current.success_rate} prev={trends.previous.success_rate} color={C.blue} format={v => v + '%'} />
            <CompareCard label="Value delivered" value={trends.current.value} prev={trends.previous.value} color="#ea580c" format={money} />
            <CompareCard label="Distance" value={trends.current.km} prev={trends.previous.km} color={C.purple} format={v => Number(v).toFixed(0) + ' km'} />
            <CompareCard label="Cancelled" value={trends.current.cancelled} prev={trends.previous.cancelled} color={C.red} invert
              onClick={() => openDrill({ status:'cancelled', title:'Cancelled' })} />
          </div>

          <Panel title="Deliveries over time" note="Click any bar to see those invoices">
            {series.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize:12 }} />
                  <Bar dataKey="Delivered" stackId="a" fill={C.green} cursor="pointer" onClick={barClick('delivered')} />
                  <Bar dataKey="Rescheduled" stackId="a" fill={C.purple} cursor="pointer" onClick={barClick('rescheduled')} />
                  <Bar dataKey="Cancelled" stackId="a" fill={C.red} cursor="pointer" onClick={barClick('cancelled')} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No data for this period" />}
          </Panel>

          <Panel title="Value delivered per day" note="Click a bar for that day's deliveries">
            {series.length ? (
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <Tooltip formatter={v => money(v)} />
                  <Bar dataKey="Value" fill="#ea580c" radius={[4,4,0,0]} cursor="pointer" onClick={barClick('delivered')} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No data for this period" />}
          </Panel>

          <Panel title="Time split per stop" note="Average travel time vs time spent at the shop">
            {series.length ? (
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} unit="m" />
                  <Tooltip formatter={v => v + ' min'} />
                  <Legend wrapperStyle={{ fontSize:12 }} />
                  <Line type="monotone" dataKey="Travel" stroke={C.blue} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Shop" stroke={C.amber} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty text="No data for this period" />}
          </Panel>
        </>
      )}

      {tab === 'drivers' && perf && (
        <>
          <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
            <StatCard icon={Users} label="Drivers active" value={drivers.filter(d => Number(d.total_stops) > 0).length} color={C.blue} sub="Selected period" />
            <StatCard icon={CheckCircle} label="Total delivered" value={num(drivers.reduce((s,d) => s + Number(d.delivered), 0))} color={C.green} sub="Selected period" />
            <StatCard icon={MapPin} label="Total distance" value={Math.round(drivers.reduce((s,d) => s + Number(d.km), 0)) + ' km'} color={C.purple} sub="Selected period" />
            <StatCard icon={TrendingUp} label="Total value" value={money(drivers.reduce((s,d) => s + Number(d.value), 0))} color="#ea580c" sub="Selected period" />
          </div>

          <Panel title="Driver performance" note="Click a column heading to sort · click a driver row for their invoices">
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:820 }}>
                <thead><tr style={{ background:'#f8fafc' }}>
                  <th style={th}>#</th><th style={th}>Driver</th>
                  {[['delivered','Delivered'],['success_rate','Success'],['stops_per_day','Stops/day'],
                    ['avg_travel','Avg travel'],['avg_service','Avg at shop'],['km','KM'],['value','Value'],
                    ['cancelled','Cancelled'],['rescheduled','Resched.']].map(([key,label]) => (
                    <th key={key} style={{ ...th, cursor:'pointer', color: sortBy===key ? C.blue : '#64748b' }}
                      onClick={() => setSortBy(key)}>{label}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {drivers.map((d, i) => (
                    <tr key={d.id} style={{ borderTop:'1px solid #f1f5f9', cursor:'pointer' }}
                      onClick={() => openDrill({ driver_id:d.id, title:`${d.name} — all stops` })}>
                      <td style={{ ...td, color:'#94a3b8', fontWeight:700 }}>{i+1}</td>
                      <td style={{ ...td, fontWeight:600, color:'#1e293b' }}>{d.name}</td>
                      <td style={{ ...td, color:C.green, fontWeight:600 }}>{d.delivered}</td>
                      <td style={td}>
                        <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700,
                          background: d.success_rate >= 80 ? '#dcfce7' : d.success_rate >= 60 ? '#fef3c7' : '#fee2e2',
                          color: d.success_rate >= 80 ? C.green : d.success_rate >= 60 ? '#b45309' : C.red }}>
                          {d.success_rate}%</span>
                      </td>
                      <td style={td}>{d.stops_per_day}</td>
                      <td style={{ ...td, color:C.blue }}>{mins(d.avg_travel)}</td>
                      <td style={{ ...td, color:C.amber }}>{mins(d.avg_service)}</td>
                      <td style={td}>{Number(d.km).toFixed(0)}</td>
                      <td style={{ ...td, color:'#1e293b' }}>{money(d.value)}</td>
                      <td style={{ ...td, color: Number(d.cancelled) > 0 ? C.red : '#94a3b8' }}>{d.cancelled}</td>
                      <td style={{ ...td, color: Number(d.rescheduled) > 0 ? C.purple : '#94a3b8' }}>{d.rescheduled}</td>
                    </tr>
                  ))}
                  {!drivers.length && <tr><td colSpan={12}><Empty text="No driver data for this period" /></td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Delivered by driver" note="Click any bar to see those invoices">
            {drivers.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={drivers.map(d => ({ name:d.name, driverId:d.id, Delivered:Number(d.delivered), Cancelled:Number(d.cancelled), Rescheduled:Number(d.rescheduled) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize:12 }} />
                  <Bar dataKey="Delivered" fill={C.green} radius={[4,4,0,0]} cursor="pointer" onClick={barClick('delivered')} />
                  <Bar dataKey="Rescheduled" fill={C.purple} radius={[4,4,0,0]} cursor="pointer" onClick={barClick('rescheduled')} />
                  <Bar dataKey="Cancelled" fill={C.red} radius={[4,4,0,0]} cursor="pointer" onClick={barClick('cancelled')} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No data" />}
          </Panel>
        </>
      )}

      {tab === 'problems' && problems && (
        <>
          <Panel title="Why deliveries fail" note="Cancellation reasons for the selected period">
            {problems.cancel_reasons?.length ? (
              <ResponsiveContainer width="100%" height={Math.max(180, problems.cancel_reasons.length * 38)}>
                <BarChart layout="vertical" data={problems.cancel_reasons.map(r => ({ reason:r.reason, Count:Number(r.count) }))} margin={{ left:120 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis type="category" dataKey="reason" tick={{ fontSize:11, fill:'#64748b' }} width={115} />
                  <Tooltip />
                  <Bar dataKey="Count" fill={C.red} radius={[0,4,4,0]} cursor="pointer"
                    onClick={() => openDrill({ status:'cancelled', title:'All cancelled deliveries' })} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No cancellations in this period" />}
          </Panel>

          <Panel title="Problem customers" note="Most cancelled or rescheduled">
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f8fafc' }}>
                {['Customer','Code','Cancelled','Rescheduled','Total issues'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(problems.problem_customers || []).map((c, i) => (
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={{ ...td, fontWeight:500, color:'#1e293b' }}>{c.customer_name}</td>
                    <td style={{ ...td, fontSize:12, color:'#94a3b8' }}>{c.customer_code}</td>
                    <td style={{ ...td, color:C.red, fontWeight:600 }}>{c.cancelled}</td>
                    <td style={{ ...td, color:C.purple, fontWeight:600 }}>{c.rescheduled}</td>
                    <td style={{ ...td, fontWeight:700, color:'#1e293b' }}>{Number(c.cancelled) + Number(c.rescheduled)}</td>
                  </tr>
                ))}
                {!problems.problem_customers?.length && <tr><td colSpan={5}><Empty text="No problem customers in this period" /></td></tr>}
              </tbody>
            </table>
          </Panel>

          <Panel title="Customers with wrong coordinates"
            note="Driver photographed the shop far from the location set in Business Central">
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f8fafc' }}>
                {['Customer','Code','Deliveries','Avg distance off','Worst'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(problems.bad_coordinates || []).map((c, i) => (
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={{ ...td, fontWeight:500, color:'#1e293b' }}>{c.customer_name}</td>
                    <td style={{ ...td, fontSize:12, color:'#94a3b8' }}>{c.customer_code}</td>
                    <td style={td}>{c.samples}</td>
                    <td style={{ ...td, color:C.red, fontWeight:700 }}>
                      {Number(c.avg_distance) >= 1000 ? (Number(c.avg_distance)/1000).toFixed(1) + ' km' : c.avg_distance + ' m'}</td>
                    <td style={td}>
                      {Number(c.max_distance) >= 1000 ? (Number(c.max_distance)/1000).toFixed(1) + ' km' : c.max_distance + ' m'}</td>
                  </tr>
                ))}
                {!problems.bad_coordinates?.length && (
                  <tr><td colSpan={5}><Empty text="No coordinate mismatches found yet — fills in as drivers use the updated app" /></td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </>
      )}

      {drill && (
        <div onClick={() => setDrill(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:1000, maxHeight:'88vh', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <h2 style={{ fontSize:16, fontWeight:700, color:'#1e293b', margin:0, textTransform:'capitalize' }}>{drill.title || 'Details'}</h2>
                {drillData && (
                  <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>
                    {drillData.count} invoices · {money(drillData.total_value)}
                    {drillData.count >= 500 && ' (showing first 500)'}
                  </div>
                )}
              </div>
              <button onClick={() => setDrill(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b' }}><X size={20} /></button>
            </div>
            <div style={{ overflow:'auto', padding:'0 4px 4px' }}>
              {drillLoading && <Empty text="Loading..." />}
              {drillData && !drillData.invoices.length && <Empty text="No invoices match this selection" />}
              {drillData && drillData.invoices.length > 0 && (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'#f8fafc', position:'sticky', top:0 }}>
                    {['Date','Invoice','Customer','Driver','Value','Status','Detail'].map(h => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {drillData.invoices.map(inv => {
                      const st = inv.delivery_status === 'cancelled' ? 'cancelled'
                        : inv.delivery_status === 'rescheduled' ? 'rescheduled'
                        : inv.status === 'delivered' ? 'delivered' : 'pending';
                      const tint = st==='delivered' ? C.green : st==='cancelled' ? C.red : st==='rescheduled' ? C.purple : '#94a3b8';
                      const bg = st==='delivered' ? '#dcfce7' : st==='cancelled' ? '#fee2e2' : st==='rescheduled' ? '#f3e8ff' : '#f1f5f9';
                      return (
                        <tr key={inv.id} style={{ borderTop:'1px solid #f1f5f9' }}>
                          <td style={{ ...td, fontSize:12 }}>{shortDate(inv.session_date)}</td>
                          <td style={{ ...td, fontSize:12, fontWeight:600, color:'#1e293b' }}>{inv.invoice_no || '—'}</td>
                          <td style={{ ...td, color:'#1e293b' }}>
                            {inv.customer_name}
                            <div style={{ fontSize:11, color:'#94a3b8' }}>{inv.customer_code} · {inv.salesman_name}</div>
                          </td>
                          <td style={{ ...td, fontSize:12 }}>{inv.driver_name}</td>
                          <td style={{ ...td, fontWeight:600, color:'#1e293b' }}>{money(inv.invoice_value)}</td>
                          <td style={td}>
                            <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, background:bg, color:tint, textTransform:'capitalize' }}>{st}</span>
                          </td>
                          <td style={{ ...td, fontSize:11, color:'#64748b' }}>
                            {inv.cancel_reason && <div style={{ color:C.red }}>{inv.cancel_reason}</div>}
                            {inv.reschedule_date && <div style={{ color:C.purple }}>To {shortDate(inv.reschedule_date)}</div>}
                            {(inv.travel_time != null || inv.service_time != null) && (
                              <div>Travel {mins(inv.travel_time)} · Shop {mins(inv.service_time)}</div>
                            )}
                            {inv.location_distance != null && (
                              <div style={{ color: inv.location_distance > 500 ? C.red : '#94a3b8' }}>
                                {inv.location_distance >= 1000 ? (inv.location_distance/1000).toFixed(1)+' km' : inv.location_distance+' m'} off
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
